import { useState, useEffect } from 'react'
import { 
  Container,
  VStack, 
  Heading, 
  Spinner, 
  Alert, 
  AlertIcon, 
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Text,
  Box,
  HStack,
  Icon,
  Wrap,
  WrapItem,
  Tooltip
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { FiMapPin, FiGlobe, FiStar, FiCheck, FiX } from 'react-icons/fi'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import DataTable from '../../../common/DataTable/DataTable'
import apiClient from '../../../../utils/apiClient'

const Locales = () => {
  const [locales, setLocales] = useState([])
  const [filteredLocales, setFilteredLocales] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  // Enhanced columns with multilingual site support
  const columns = [
    { 
      key: 'language_info', 
      label: 'Language',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Icon as={FiMapPin} color="purple.500" size="sm" />
            <Text fontWeight="medium">{row.name}</Text>
          </HStack>
          <HStack spacing={2}>
            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
              {row.code}
            </Badge>
            {row.native_name && row.native_name !== row.name && (
              <Badge colorScheme="blue" variant="outline" fontSize="xs">
                {row.native_name}
              </Badge>
            )}
          </HStack>
        </VStack>
      )
    },
    { 
      key: 'site_assignments', 
      label: 'Site Assignments',
      render: (value, row) => (
        <VStack align="start" spacing={2}>
          <HStack>
            <Icon as={FiGlobe} color="blue.500" size="sm" />
            <Text fontSize="sm" fontWeight="medium">
              {row.totalSites} Site{row.totalSites !== 1 ? 's' : ''}
            </Text>
          </HStack>
          
          {row.siteAssignments && row.siteAssignments.length > 0 ? (
            <VStack align="start" spacing={1}>
              <Wrap spacing={1}>
                {row.siteAssignments.slice(0, 2).map(site => (
                  <WrapItem key={site.site_id}>
                    <Tooltip 
                      label={`${site.site_name}${site.is_default ? ' (Default)' : ''}${!site.is_active ? ' (Inactive)' : ''}`}
                    >
                      <Badge 
                        size="sm"
                        colorScheme={site.is_default ? 'yellow' : site.is_active ? 'green' : 'gray'}
                        variant={site.is_default ? 'solid' : 'subtle'}
                      >
                        {site.site_name.length > 10 ? site.site_name.substring(0, 10) + '...' : site.site_name}
                        {site.is_default && ' ★'}
                      </Badge>
                    </Tooltip>
                  </WrapItem>
                ))}
                {row.siteAssignments.length > 2 && (
                  <WrapItem>
                    <Badge size="sm" variant="outline">
                      +{row.siteAssignments.length - 2}
                    </Badge>
                  </WrapItem>
                )}
              </Wrap>
              
              <HStack spacing={3} fontSize="xs" color="gray.500">
                <HStack>
                  <Icon as={FiCheck} color="green.500" />
                  <Text>{row.activeSites} Active</Text>
                </HStack>
                <HStack>
                  <Icon as={FiStar} color="yellow.500" />
                  <Text>{row.defaultSites} Default</Text>
                </HStack>
              </HStack>
            </VStack>
          ) : (
            <HStack>
              <Icon as={FiX} color="red.500" size="sm" />
              <Text fontSize="sm" color="red.500">
                No assignments
              </Text>
            </HStack>
          )}
        </VStack>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Badge 
            colorScheme={row.totalSites > 0 ? 'green' : 'gray'}
            variant="subtle"
          >
            {row.totalSites > 0 ? 'Active' : 'Inactive'}
          </Badge>
          {row.totalSites > 0 && (
            <Text fontSize="xs" color="gray.500">
              {row.activeSites}/{row.totalSites} sites active
            </Text>
          )}
        </VStack>
      )
    }
  ]

  const fetchLocales = async () => {
    if (!tenantId) return

    try {
      const response = await apiClient(`/locales/${tenantId}`)
      console.log('Fetched locales with site assignments:', response)

      const formattedLocales = response.map(locale => {
        const siteAssignments = locale.sites || []
        const totalSites = siteAssignments.length
        const activeSites = siteAssignments.filter(site => site.is_active).length
        const defaultSites = siteAssignments.filter(site => site.is_default).length

        return {
          id: locale.id,
          name: locale.name,
          code: locale.code,
          native_name: locale.native_name,
          siteAssignments: siteAssignments,
          totalSites: totalSites,
          activeSites: activeSites,
          defaultSites: defaultSites
        }
      })

      setLocales(formattedLocales)
      setFilteredLocales(formattedLocales)
    } catch (err) {
      setError(err.message || 'Failed to load locales')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLocales()
  }, [tenantId])

  const handleSearch = (searchTerm) => {
    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = locales.filter(locale =>
      locale.name.toLowerCase().includes(lowercaseSearch) ||
      locale.code.toLowerCase().includes(lowercaseSearch) ||
      (locale.native_name && locale.native_name.toLowerCase().includes(lowercaseSearch)) ||
      locale.siteAssignments.some(site => 
        site.site_name.toLowerCase().includes(lowercaseSearch)
      )
    )
    setFilteredLocales(filtered)
  }

  const handleEdit = (locale) => {
    navigate(`/settings/locales/edit/${locale.id}`)
  }

  const handleCreate = () => {
    navigate(`/settings/locales/create`)
  }

  const handleDelete = async (locale) => {
    try {
      await apiClient(`/locales/${tenantId}/${locale.id}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: 'Locale deleted successfully',
        status: 'success',
        duration: 3000
      })

      fetchLocales()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete locale',
        status: 'error',
        duration: 3000
      })
    }
  }

  // Calculate enhanced statistics
  const getLocaleStats = () => {
    const total = locales.length
    const assigned = locales.filter(locale => locale.totalSites > 0).length
    const unassigned = locales.filter(locale => locale.totalSites === 0).length
    const multiSite = locales.filter(locale => locale.totalSites > 1).length
    const defaultLocales = locales.filter(locale => locale.defaultSites > 0).length
    
    return { total, assigned, unassigned, multiSite, defaultLocales }
  }

  const stats = getLocaleStats()

  if (loading) {
    return (
      <Container maxW="container.2xl" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading locales...</Text>
        </VStack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="container.2xl" py={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Locales</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <HStack mb={2}>
            <Icon as={FiMapPin} color="purple.500" boxSize={6} />
            <Heading size="lg">Locales Management</Heading>
          </HStack>
          <Text color="gray.500">
            Manage language locales and their assignments to sites. Configure multilingual support with default fallback languages.
          </Text>
        </Box>

        {/* Enhanced Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Locales</StatLabel>
                <StatNumber color="purple.500">{stats.total}</StatNumber>
                <StatHelpText>All configured languages</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Assigned</StatLabel>
                <StatNumber color="green.500">{stats.assigned}</StatNumber>
                <StatHelpText>Active on sites</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Multi-Site</StatLabel>
                <StatNumber color="blue.500">{stats.multiSite}</StatNumber>
                <StatHelpText>Used by multiple sites</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Default Locales</StatLabel>
                <StatNumber color="yellow.500">{stats.defaultLocales}</StatNumber>
                <StatHelpText>Set as fallback</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Unassigned</StatLabel>
                <StatNumber color="gray.500">{stats.unassigned}</StatNumber>
                <StatHelpText>Not assigned to sites</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Warnings */}
        {stats.unassigned > 0 && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="medium">
                {stats.unassigned} locale{stats.unassigned > 1 ? 's' : ''} not assigned to any sites
              </Text>
              <Text fontSize="sm">
                Unassigned locales won't be available for content creation. Consider assigning them to sites or removing them.
              </Text>
            </VStack>
          </Alert>
        )}

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredLocales}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search locales by language, code, native name, or assigned sites..."
              isLoading={loading}
              title="Locales Management"
              createButtonText="Create New Locale"
              emptyStateTitle="No locales found"
              emptyStateDescription={
                filteredLocales.length === 0 && locales.length > 0
                  ? "No locales match your search criteria. Try adjusting your search terms."
                  : "Get started by creating your first locale to support multiple languages across your sites."
              }
              loadingText="Loading locales..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default Locales
